import type { CompanyResponse, UpdateCompanyDto } from '@super/shared-types'
import { BaseApiClient } from './base-client'
import { API_ROUTES, interpolateRoute } from '../api-routes'

/**
 * Company search parameters
 */
export interface CompanySearchParams {
  q?: string
  orgId?: string
  companyId?: string
  limit?: number
  offset?: number
}

/**
 * Company search response
 */
export interface CompanySearchResponse {
  companies: CompanyResponse[]
  total: number
  limit: number
  offset: number
}

/**
 * API response wrapper
 */
interface ApiResponse<T = any> {
  data: T
  success?: boolean
  message?: string
}

/**
 * Companies API Client
 * Handles all company-related API operations
 */
export class CompaniesClient extends BaseApiClient {
  /**
   * Get current user's company
   */
  async getCurrentCompany(): Promise<CompanyResponse> {
    const response = await this.request<ApiResponse<CompanyResponse>>(
      API_ROUTES.COMPANIES.GET,
    )
    return response.data
  }

  /**
   * Update company
   * @param companyId - The ID of the company to update
   * @param data - The data to update the company with
   * @returns The updated company
   */
  async updateCompany(
    companyId: string,
    data: UpdateCompanyDto,
  ): Promise<CompanyResponse> {
    const response = await this.request<ApiResponse<CompanyResponse>>(
      interpolateRoute(API_ROUTES.COMPANIES.UPDATE, { id: companyId }),
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
    )
    return response.data
  }

  /**
   * Search companies (Super Data Admin only)
   * @param params - The companysearch parameters
   * @returns The search results of companies
   */
  async searchCompanies(
    params: CompanySearchParams = {},
  ): Promise<CompanySearchResponse> {
    const queryString = this.buildQueryString(params)
    const path = `${API_ROUTES.ADMIN.COMPANIES.SEARCH}${queryString}`

    const response =
      await this.request<ApiResponse<CompanySearchResponse>>(path)
    return response.data
  }

  /**
   * Get company by ID (Super Data Admin only)
   * @param id - The ID of the company to get
   * @returns The company with the given ID
   */
  async getCompanyById(id: string): Promise<CompanyResponse> {
    const response = await this.request<ApiResponse<CompanyResponse>>(
      interpolateRoute(API_ROUTES.ADMIN.COMPANIES.GET_BY_ID, { id }),
    )
    return response.data
  }
}

/**
 * Default companies client instance
 */
export const companiesClient = new CompaniesClient()
